import Vue from "vue";
import { Module } from "vuex";

import { api } from "@/util/axios";
import { Blog, Page } from "@/types";

type BlogState = {
  blogPage: Page<Blog>;
};

const blog: Module<BlogState, unknown> = {
  namespaced: true,
  state: () => ({
    blogPage: {
      docs: [],
      limit: 0,
      page: 0,
      pages: 0,
      sort: "",
      total: 0,
    },
  }),
  getters: {
    getBlog: (state) => (blogId: string) =>
      state.blogPage.docs.find((b) => b._id === blogId),
    blogPage: (state) => state.blogPage,
  },
  mutations: {
    ADD_BLOG: (state, _blog) => {
      let idx = -1;
      state.blogPage.docs.map((b, i) => {
        if (b._id === _blog._id) idx = i;
      });
      if (idx === -1) state.blogPage.docs.push(_blog);
      else Vue.set(state.blogPage.docs, idx, _blog);
    },
    SET_BLOG_PAGE: (state, list) => {
      state.blogPage = list;
    },
    REMOVE_BLOG(state, blog) {
      let idx = -1;
      state.blogPage.docs.map((b, i) => {
        if (b._id === blog._id) idx = i;
      });
      if (idx > -1) state.blogPage.docs.splice(idx, 1);
    },
  },
  actions: {
    fetchBlog(context, params: { businessId: string; blogId: string }) {
      api
        .get(`/v1/business/${params.businessId}/blog/${params.blogId}`)
        .then((response) => {
          context.commit("ADD_BLOG", response.data.blog);
        })
        .catch((error) => {
          context.dispatch(
            "setToast",
            {
              title: "Request failed!",
              type: "error",
              text: error.response?.data?.error?.message,
            },
            { root: true }
          );
        });
    },
    fetchBlogBySlug(context, params: { businessId: string; slug: string }) {
      api
        .get(`/v1/business/${params.businessId}/blog/by-slug/${params.slug}`)
        .then((response) => {
          context.commit("ADD_BLOG", response.data.blog);
        })
        .catch((error) => {
          context.dispatch(
            "setToast",
            {
              title: "Request failed!",
              type: "error",
              text: error.response?.data?.error?.message,
            },
            { root: true }
          );
        });
    },
    fetchBlogList(context, params: { businessId: string; _status: string }) {
      const businessId = params.businessId;
      api
        .get(`/v1/business/${businessId}/blog?_status=${params._status}`)
        .then((response) => {
          context.commit("SET_BLOG_PAGE", response.data.blogPage);
        })
        .catch((error) => {
          context.dispatch(
            "setToast",
            {
              title: "Request failed!",
              type: "error",
              text: error.response?.data?.error?.message,
            },
            { root: true }
          );
        });
    },
    async createBlog(context, payload: { businessId: string; data: FormData }) {
      try {
        // Add logging to debug the request
        console.log("Creating blog with payload:", {
          businessId: payload.businessId,
          formDataEntries: Array.from(payload.data.entries()).map(
            ([key, value]) => {
              if (value instanceof File) {
                return [key, `File: ${value.name}`];
              }
              return [key, value];
            }
          ),
        });

        const response = await api.post(
          `/v1/business/${payload.businessId}/blog`,
          payload.data,
          {
            headers: {
              "content-type": "multipart/form-data",
            },
          }
        );
        context.commit("ADD_BLOG", response.data.blog);
        context.dispatch(
          "setToast",
          {
            title: "Success",
            type: "success",
            text: "Blog post created",
          },
          { root: true }
        );
        return response.data.blog;
      } catch (error: any) {
        console.error("Error creating blog post:", error.response || error);
        context.dispatch(
          "setToast",
          {
            title: "Request failed!",
            type: "error",
            text:
              error.response?.data?.error?.message ||
              "Failed to create blog post",
          },
          { root: true }
        );
        throw error;
      }
    },
    async updateBlog(
      context,
      payload: { businessId: string; blogId: string; data: FormData }
    ) {
      try {
        // Add logging to debug the request
        console.log("Updating blog with payload:", {
          businessId: payload.businessId,
          blogId: payload.blogId,
          formDataEntries: Array.from(payload.data.entries()).map(
            ([key, value]) => {
              if (value instanceof File) {
                return [key, `File: ${value.name}`];
              }
              return [key, value];
            }
          ),
        });

        const response = await api.put(
          `/v1/business/${payload.businessId}/blog/${payload.blogId}`,
          payload.data
        );
        context.commit("ADD_BLOG", response.data.blog);
        context.dispatch(
          "setToast",
          {
            title: "Success",
            type: "success",
            text: "Blog post updated",
          },
          { root: true }
        );
        return response.data.blog;
      } catch (error: any) {
        console.error("Error updating blog post:", error.response || error);
        context.dispatch(
          "setToast",
          {
            title: "Request failed!",
            type: "error",
            text:
              error.response?.data?.error?.message ||
              "Failed to update blog post",
          },
          { root: true }
        );
        throw error;
      }
    },
    deleteBlog(context, params: { businessId: string; blogId: string }) {
      api
        .delete(`/v1/business/${params.businessId}/blog/${params.blogId}`)
        .then((response) => {
          context.commit("REMOVE_BLOG", response.data.blog);
          context.dispatch(
            "setToast",
            {
              title: "Success",
              type: "success",
              text: "Blog post deleted",
            },
            { root: true }
          );
        })
        .catch((error) => {
          context.dispatch(
            "setToast",
            {
              title: "Request failed!",
              type: "error",
              text: error.response?.data?.error?.message,
            },
            { root: true }
          );
        });
    },
  },
};

export default blog;
